import type {
    CubeSignerClient,
} from "@cubist-labs/cubesigner-sdk";
import * as cs from "@cubist-labs/cubesigner-sdk";
import { store } from "../redux/store";
import { ebioroApi } from "../api/ebioro/ebioro";
import { ok, err, Result } from "neverthrow";
import { Networks, Transaction } from "@stellar/stellar-sdk";
import { Buffer } from 'buffer';

// Environment configuration
const isTestnet = import.meta.env.VITE_isTestnet === 'true';
const cubesignerEnv = isTestnet ? cs.envs.gamma : cs.envs.prod;
const cubesignerOrgId = import.meta.env.VITE_CUBESIGNER_ORG_ID;

let client: CubeSignerClient | null = null;

// Function to get partial token from API (non-hook version)
const getAccountRecoveryPartialToken = async (email: string, authToken: string): Promise<{ partial_token: string } | null> => {
    try {
        const result = await store.dispatch(
            ebioroApi.endpoints.getAccountRecoveryPartialToken.initiate({
                email,
                auth_token: authToken
            })
        );

        if (result.data) {
            return result.data;
        } else {
            console.error('Failed to get partial token:', result.error);
            return null;
        }
    } catch (error) {
        console.error('Error getting partial token:', error);
        return null;
    }
};

// Function to reconstruct OIDC token from partial token and signature
const reconstructOidcToken = (partialToken: string, signature: string): string => {
    return partialToken + signature.trim();
};

// Login function that obtains OIDC token and creates CubeSigner session
const login = async (partial_token: string, signature: string): Promise<Result<CubeSignerClient, Error>> => {

    try {

        // Step 2: Reconstruct complete OIDC token
        const id_token = reconstructOidcToken(partial_token, signature);

        // Step 3: Create OIDC session with CubeSigner
        const session = await cs.CubeSignerClient.createOidcSession(
            cubesignerEnv,
            cubesignerOrgId,
            id_token,
            [
                "manage:readonly", // for listing keys
                "sign:blob", // for signing blob stellar transactions
            ]
        );

        // Step 4: Create client from session data
        client = await cs.CubeSignerClient.create(session.data());

        return ok(client);

    } catch (error) {

        const errObj = error instanceof Error ? error : new Error('Login failed');
        return err(errObj);

    }
};

// Get the current CubeSigner client
const getClient = (): CubeSignerClient | null => {
    return client;
};

// Check if user is logged in
const isLoggedIn = (): boolean => {
    return client !== null;
};

// Logout function
const logout = (): void => {
    client = null;
    console.log('Logged out successfully');
};

const signTx = async (txXdr: string, address: string, client: CubeSignerClient): Promise<string> => {

    if (!client) {
        throw new Error('CubeSigner client not initialized. Please login first.');
    }

    try {

        const key = `Key#Stellar_${address}`;
        const stellarTx = new Transaction(txXdr, isTestnet ? Networks.TESTNET : Networks.PUBLIC);
        const signable = stellarTx.hash().toString('base64');
        const blobReq: cs.BlobSignRequest = {
            message_base64: signable
        };

        const response = await client.apiClient.signBlob(key, blobReq);

        const signatureResult = getBase64Signature(response.data().signature);

        if (signatureResult.isErr()) {
            throw signatureResult.error;
        }

        const signature = signatureResult.value;

        stellarTx.addSignature(address, signature);

        const signedXdr = stellarTx.toXDR();

        return signedXdr;

    } catch (error) {
        console.error('Transaction signing failed:', error);
        throw error;
    }
};

const getBase64Signature = (hexSignature: string): Result<string, Error> => {
    try {
        const cleanHex = hexSignature.startsWith('0x') ? hexSignature.slice(2) : hexSignature;
        const buffer = Buffer.from(cleanHex, 'hex');
        return ok(buffer.toString('base64'));
    } catch (error) {

        return err(new Error('Error converting hex to base64 signature'));
    }
}

// Export the functions
export {
    login,
    logout,
    signTx,
    getClient,
    isLoggedIn,
    getAccountRecoveryPartialToken,
    reconstructOidcToken
};